import React, { useState } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon, SignalIcon, SignalSlashIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

interface ErrorRecoveryProps {
  error: Error;
  onRetry: () => void;
  onSkip?: () => void;
  onGoBack?: () => void;
  isRetrying?: boolean;
}

export function ErrorRecovery({ 
  error, 
  onRetry, 
  onSkip, 
  onGoBack, 
  isRetrying = false 
}: ErrorRecoveryProps) {
  const { isOnline, isSlowConnection } = useNetworkStatus();
  const [showDetails, setShowDetails] = useState(false);

  const getErrorType = () => {
    if (!isOnline) return 'offline';
    if (isSlowConnection) return 'slow';
    if (error.message.includes('timeout')) return 'timeout';
    if (error.message.includes('network')) return 'network';
    if (error.message.includes('server')) return 'server';
    if (error.message.includes('rate limit') || error.message.includes('429')) return 'rate_limit';
    if (error.message.includes('already exists') || 
        error.message.includes('already registered') ||
        error.message.includes('An account with this email already exists')) return 'user_exists';
    return 'unknown';
  };

  const getErrorIcon = () => {
    const errorType = getErrorType();
    switch (errorType) {
      case 'offline':
        return <SignalSlashIcon className="w-6 h-6 text-red-500" />;
      case 'slow':
        return <ClockIcon className="w-6 h-6 text-yellow-500" />;
      case 'timeout':
        return <ClockIcon className="w-6 h-6 text-orange-500" />;
      case 'rate_limit':
        return <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500" />;
      case 'user_exists':
        return <ExclamationTriangleIcon className="w-6 h-6 text-blue-500" />;
      default:
        return <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />;
    }
  };

  const getErrorMessage = () => {
    const errorType = getErrorType();
    switch (errorType) {
      case 'offline':
        return 'You\'re currently offline. Please check your internet connection and try again.';
      case 'slow':
        return 'Your connection seems slow. This might take a moment...';
      case 'timeout':
        return 'The request timed out. This might be due to a slow connection.';
      case 'network':
        return 'Network error occurred. Please check your connection and try again.';
      case 'server':
        return 'Server error occurred. Our team has been notified.';
      case 'rate_limit':
        return 'Too many signup attempts. Please wait a few minutes before trying again.';
      case 'user_exists':
        return 'An account with this email already exists. Please sign in instead.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  const getSuggestions = () => {
    const errorType = getErrorType();
    switch (errorType) {
      case 'offline':
        return [
          'Check your Wi-Fi or mobile data connection',
          'Try refreshing the page',
          'Move to an area with better signal'
        ];
      case 'slow':
        return [
          'Wait a moment for the connection to improve',
          'Try refreshing the page',
          'Close other apps that might be using bandwidth'
        ];
      case 'timeout':
        return [
          'Check your internet connection speed',
          'Try again in a moment',
          'Contact support if the issue persists'
        ];
      case 'rate_limit':
        return [
          'Wait 5-10 minutes before trying again',
          'Try using a different email address',
          'Contact support if you need immediate access'
        ];
      case 'user_exists':
        return [
          'Use the "Sign In" button below to access your existing account',
          'Try using a different email address',
          'Contact support if you need help accessing your account'
        ];
      default:
        return [
          'Try refreshing the page',
          'Check your internet connection',
          'Contact support if the issue continues'
        ];
    }
  };

  const errorType = getErrorType();
  const isUserExists = errorType === 'user_exists';
  
  return (
    <div className={`${isUserExists ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-4`}>
      <div className="flex items-start space-x-3">
        {getErrorIcon()}
        <div className="flex-1">
          <h3 className={`text-sm font-medium ${isUserExists ? 'text-blue-800' : 'text-red-800'} mb-1`}>
            {getErrorMessage()}
          </h3>
          
          <div className="flex flex-wrap gap-2 mt-3">
            {getErrorType() === 'user_exists' ? (
              <>
                <button
                  onClick={() => window.location.href = '/login'}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Sign In
                </button>
                <button
                  onClick={onGoBack}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Use Different Email
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onRetry}
                  disabled={isRetrying || !isOnline}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowPathIcon className={`w-3 h-3 mr-1 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying ? 'Retrying...' : 'Try Again'}
                </button>

                {onGoBack && (
                  <button
                    onClick={onGoBack}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                  >
                    Go Back
                  </button>
                )}

                {onSkip && (
                  <button
                    onClick={onSkip}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                  >
                    Skip for Now
                  </button>
                )}
              </>
            )}

            <button
              onClick={() => setShowDetails(!showDetails)}
              className={`inline-flex items-center px-3 py-1.5 text-xs font-medium ${isUserExists ? 'text-blue-700 bg-blue-100 hover:bg-blue-200' : 'text-red-700 bg-red-100 hover:bg-red-200'} rounded-md`}
            >
              {showDetails ? 'Hide' : 'Show'} Details
            </button>
          </div>

          {showDetails && (
            <div className={`mt-3 p-3 ${isUserExists ? 'bg-blue-100' : 'bg-red-100'} rounded-md`}>
              <p className={`text-xs ${isUserExists ? 'text-blue-700' : 'text-red-700'} mb-2`}>
                <strong>Error details:</strong> {error.message}
              </p>
              <div className={`text-xs ${isUserExists ? 'text-blue-600' : 'text-red-600'}`}>
                <strong>Suggestions:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {getSuggestions().map((suggestion, index) => (
                    <li key={index}>{suggestion}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
